import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, TreePine, Users, Briefcase, Scale, PiggyBank, GraduationCap, Eye, EyeOff } from 'lucide-react';
import { getSafeRedirectUrl } from '../lib/safeRedirect';
import { AUTH_ENABLED } from '../lib/featureFlags';
import { MaintenanceNotice } from '../components/common/MaintenanceNotice';

const Register = () => {
  const { language } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userType, setUserType] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Get SAFE redirect URL from query params (validates against open redirect attacks)
  const redirectUrl = getSafeRedirectUrl(searchParams.get('redirect'), '/verktyg');
  const hasRedirect = searchParams.get('redirect') !== null;
  const purchaseIntent = searchParams.get('intent') === 'purchase';
  
  // Track register page view
  useEffect(() => {
  }, [hasRedirect]);

  // Wartemodus: after all hooks have been registered (rules-of-hooks compliant)
  // show a calm notice card instead of an active registration form.
  if (!AUTH_ENABLED) {
    return <MaintenanceNotice variant="page" showBackLink testId="register-maintenance" />;
  }

  const translations = {
    sv: {
      title: 'Skapa ett konto',
      subtitle: 'Spara dina beräkningar, jämför resultat och få tillgång till fler funktioner',
      name: 'Namn',
      namePlaceholder: 'Ditt namn',
      email: 'E-post',
      emailPlaceholder: 'namn@example.com',
      password: 'Lösenord',
      passwordPlaceholder: 'Minst 6 tecken',
      userType: 'Användartyp',
      userTypePlaceholder: 'Välj din roll (valfritt)',
      country: 'Land',
      countryPlaceholder: 'Ditt land (valfritt)',
      submit: 'Skapa konto',
      loading: 'Skapar konto...',
      hasAccount: 'Har du redan ett konto?',
      signIn: 'Logga in',
      userTypes: {
        forest_owner: 'Skogsägare',
        advisor: 'Rådgivare / Skogsförvaltare',
        broker: 'Mäklare / Värderare',
        investor: 'Investerare',
        student: 'Student / Utbildning'
      },
      errors: {
        fillAll: 'Fyll i namn, e-post och lösenord',
        passwordLength: 'Lösenordet måste vara minst 6 tecken',
        failed: 'Registrering misslyckades'
      },
      success: 'Konto skapat!'
    },
    de: {
      title: 'Konto erstellen',
      subtitle: 'Speichern Sie Ihre Berechnungen, vergleichen Sie Ergebnisse und erhalten Sie Zugang zu weiteren Funktionen',
      name: 'Name',
      namePlaceholder: 'Ihr Name',
      email: 'E-Mail',
      emailPlaceholder: 'name@beispiel.de',
      password: 'Passwort',
      passwordPlaceholder: 'Mindestens 6 Zeichen',
      userType: 'Benutzertyp',
      userTypePlaceholder: 'Wählen Sie Ihre Rolle (optional)',
      country: 'Land',
      countryPlaceholder: 'Ihr Land (optional)',
      submit: 'Konto erstellen',
      loading: 'Konto wird erstellt...',
      hasAccount: 'Haben Sie bereits ein Konto?',
      signIn: 'Anmelden',
      userTypes: {
        forest_owner: 'Waldbesitzer',
        advisor: 'Berater / Forstverwalter',
        broker: 'Makler / Gutachter',
        investor: 'Investor',
        student: 'Student / Ausbildung'
      },
      errors: {
        fillAll: 'Bitte füllen Sie Name, E-Mail und Passwort aus',
        passwordLength: 'Passwort muss mindestens 6 Zeichen haben',
        failed: 'Registrierung fehlgeschlagen'
      },
      success: 'Konto erstellt!'
    },
    en: {
      title: 'Create an account',
      subtitle: 'Save your calculations, compare results and get access to more features',
      name: 'Name',
      namePlaceholder: 'Your name',
      email: 'Email',
      emailPlaceholder: 'name@example.com',
      password: 'Password',
      passwordPlaceholder: 'At least 6 characters',
      userType: 'User type',
      userTypePlaceholder: 'Select your role (optional)',
      country: 'Country',
      countryPlaceholder: 'Your country (optional)',
      submit: 'Create account',
      loading: 'Creating account...',
      hasAccount: 'Already have an account?',
      signIn: 'Sign in',
      userTypes: {
        forest_owner: 'Forest owner',
        advisor: 'Advisor / Forest manager',
        broker: 'Broker / Valuer',
        investor: 'Investor',
        student: 'Student / Education'
      },
      errors: {
        fillAll: 'Please fill in name, email and password',
        passwordLength: 'Password must be at least 6 characters',
        failed: 'Registration failed'
      },
      success: 'Account created!'
    },
    se: {
      title: 'Ráhkat konto',
      subtitle: 'Registrere nuvttá geavahit buot rehkenastimiid',
      name: 'Namma',
      namePlaceholder: 'Du namma',
      email: 'E-poasta',
      emailPlaceholder: 'namma@ovdamearka.se',
      password: 'Beassansátni',
      passwordPlaceholder: 'Unnimustá 6 mearka',
      userType: 'Geavaheaddjitiipa',
      userTypePlaceholder: 'Vállje du rolla (válljemušlaš)',
      country: 'Riika',
      countryPlaceholder: 'Du riika (válljemušlaš)',
      submit: 'Ráhkat konto',
      loading: 'Ráhkadeame konto...',
      hasAccount: 'Lea go dus konto juo?',
      signIn: 'Čále sisa',
      userTypes: {
        forest_owner: 'Meahcceeaiggát',
        advisor: 'Ráđđeaddji / Meahccehálddašeaddji',
        broker: 'Máklár / Árvvoštalli',
        investor: 'Investor',
        student: 'Studeanta / Oahpahus'
      },
      errors: {
        fillAll: 'Deavdde nama, e-poastta ja beassansáni',
        passwordLength: 'Beassansátni ferte leat unnimustá 6 mearka',
        failed: 'Registreren ii lihkosmuvvan'
      },
      success: 'Konto ráhkaduvvon!'
    }
  };

  const t = translations[language] || translations.sv;

  const userTypeOptions = [
    { value: 'forest_owner', icon: Users },
    { value: 'advisor', icon: Briefcase },
    { value: 'broker', icon: Scale },
    { value: 'investor', icon: PiggyBank },
    { value: 'student', icon: GraduationCap }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error(t.errors.fillAll);
      return;
    }
    if (password.length < 6) {
      toast.error(t.errors.passwordLength);
      return;
    }

    setLoading(true);
    try {
      const userData = await register(email, password, name, userType, country);
      console.log('Registration successful:', userData);
      toast.success(t.success);
      setTimeout(() => {
        navigate(redirectUrl, { replace: true });
      }, 100);
    } catch (error) {
      console.error('Registration error:', error);
      const status = error.response?.status;
      let message;
      if (status === 429) {
        message = 'För många registreringsförsök. Vänta en stund och försök igen.';
      } else {
        message = error.response?.data?.detail || t.errors.failed;
      }
      toast.error(message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center" data-testid="register-page">
      <div className="w-full max-w-md">
        <Card className="animate-fadeIn">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 rounded-full bg-forest-100 w-fit">
              <TreePine className="h-8 w-8 text-forest-700" />
            </div>
            <CardTitle className="text-2xl font-heading">{t.title}</CardTitle>
            <CardDescription>{t.subtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            {purchaseIntent && (
              <div
                className="mb-4 rounded-lg border border-forest-300 bg-forest-50 p-3 text-sm text-forest-800"
                data-testid="purchase-intent-banner-register"
              >
                <p className="font-medium">Skapa konto för att slutföra ditt köp</p>
                <p className="text-forest-700 mt-1">
                  Vi behöver ett konto så att vi kan spara ditt beslutsbrev och skicka kvittot. Det tar 30 sekunder, dina värden bevaras.
                </p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t.name} *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t.namePlaceholder}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  data-testid="register-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t.email} *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  data-testid="register-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t.password} *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t.passwordPlaceholder}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    data-testid="register-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowPassword((p) => !p);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 focus:outline-none cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                    tabIndex={-1}
                    aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" style={{ pointerEvents: 'none' }} />
                    ) : (
                      <Eye className="h-5 w-5" style={{ pointerEvents: 'none' }} />
                    )}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="userType">{t.userType}</Label>
                <Select value={userType} onValueChange={setUserType} disabled={loading}>
                  <SelectTrigger data-testid="register-user-type">
                    <SelectValue placeholder={t.userTypePlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {userTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <option.icon className="h-4 w-4 text-forest-600" />
                          {t.userTypes[option.value]}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">{t.country}</Label>
                <Input
                  id="country"
                  type="text"
                  placeholder={t.countryPlaceholder}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  disabled={loading}
                  data-testid="register-country"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t.loading}
                  </>
                ) : (
                  t.submit
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-sm text-muted-foreground">
              {t.hasAccount}{' '}
              <Link
                to={`/login${
                  hasRedirect
                    ? `?redirect=${encodeURIComponent(searchParams.get('redirect'))}${purchaseIntent ? '&intent=purchase' : ''}`
                    : ''
                }`}
                className="text-forest-600 hover:underline"
                data-testid="go-to-login"
              >
                {t.signIn}
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Register;
